import { Outlet, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sidebar } from './Sidebar'
import { NotificationBell } from '../NotificationBell'
import { SipStatusIcon } from '../SipStatusIcon'
import { useAuthStore } from '../../store/auth'
import { adminApi, companySettingsApi } from '../../api/settings'

const PBX_CERT_URL = 'https://84.247.177.107:8089/ws'
const PBX_CERT_KEY = 'pbx_cert_accepted'

function PbxCertBanner() {
  const [dismissed, setDismissed] = useState(
    () => !!localStorage.getItem(PBX_CERT_KEY),
  )
  if (dismissed) return null
  return (
    <div className="flex items-center justify-between gap-4 bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 flex-shrink-0">
      <span>
        To enable phone calls from the browser, first{' '}
        <a
          href={PBX_CERT_URL}
          target="_blank"
          rel="noreferrer"
          className="underline font-semibold hover:text-amber-900"
        >
          open this link
        </a>
        , accept the security warning in that tab, then click →
      </span>
      <button
        onClick={() => { localStorage.setItem(PBX_CERT_KEY, '1'); setDismissed(true) }}
        className="flex-shrink-0 px-2.5 py-1 bg-amber-200 hover:bg-amber-300 rounded text-amber-900 font-medium transition-colors whitespace-nowrap"
      >
        I accepted it
      </button>
    </div>
  )
}

export function AppLayout() {
  const { companyId } = useParams<{ companyId: string }>()
  const { user } = useAuthStore()

  const { data: companyData } = useQuery({
    queryKey: ['company-header', companyId, user?.role],
    queryFn: async () => {
      if (!companyId) return null
      if (user?.role === 'administrator') {
        const response = await adminApi.getCompany(companyId)
        return { name: String(response.data?.name ?? ''), logoUrl: response.data?.logoUrl ?? null }
      }
      if (user?.role === 'company_owner' || user?.role === 'sales_user') {
        const response = await companySettingsApi.getGeneral(companyId)
        return { name: String(response.data?.name ?? ''), logoUrl: response.data?.logoUrl ?? null }
      }
      return null
    },
    enabled: Boolean(companyId && user),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const companyName = companyData?.name || 'Company'

  // Baseline tab title inside a company workspace: the company name. Pages set
  // their own (e.g. "Recordings - Acme") via usePageTitle; this only fills in
  // when no page title is set yet, so the tab never falls back to the bare app
  // default. The guard avoids clobbering a page title (child effects run first).
  useEffect(() => {
    if (companyData?.name && document.title === 'CRM') {
      document.title = companyData.name
    }
  }, [companyData?.name])

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar companyName={companyData?.name ?? undefined} logoUrl={companyData?.logoUrl ?? undefined} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <PbxCertBanner />
        <header className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4 gap-2 flex-shrink-0">
          <span className="text-sm font-semibold text-gray-800">{companyName}</span>
          <div className="flex items-center gap-1">
            <SipStatusIcon />
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

// Re-export for convenience
export { useAuthStore }
